import React, { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../auth";
import {
  FaBuilding,
  FaUserCircle,
  FaClock,
  FaEnvelope,
  FaIdBadge,
  FaPhoneAlt,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.alumni.uem.mz";
const ME_URL = `${API_BASE}/api/v1/users/my/`;

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 break-words">{value ?? "—"}</div>
    </div>
  );
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);

  useEffect(() => {
    let aborted = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const resp = await authFetch(ME_URL);
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Erro ${resp.status}`);
        }
        const data = await resp.json();
        if (!aborted) setMe(data);
      } catch (e) {
        if (!aborted) setError(e?.message || "Erro ao carregar perfil");
      } finally {
        if (!aborted) setLoading(false);
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, []);

  const displayName = useMemo(() => {
    if (!me) return "";
    const fn = (me.first_name || "").trim();
    const ln = (me.last_name || "").trim();
    const full = `${fn} ${ln}`.trim();
    return full || me.username || "Utilizador";
  }, [me]);

  return (
    <div className="min-h-screen max-w-5xl mx-auto pt-16 md:pt-20 pb-28 md:pb-24 px-4 sm:px-5 md:px-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
            Meu Perfil
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Dados carregados de{" "}
            <span className="font-mono text-xs">{`/api/v1/users/my/`}</span>
          </p>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">A carregar…</div>}
      {error && <div className="text-sm text-red-600 break-words">{error}</div>}

      {!loading && !error && me && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 md:gap-6 min-w-0">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 min-w-0">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                <FaUserCircle className="text-gray-400" size={34} />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  {displayName}
                </div>
                <div className="text-sm text-gray-600 truncate">{me.username}</div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Primeiro nome" value={me.first_name} />
              <Field label="Último nome" value={me.last_name} />
              <Field label="Empresa" value={me.company} />
              <Field label="Email pessoal" value={me.personal_email} />
              <Field label="Contacto" value={me.contact_number} />
              <Field label="Sobre" value={me.about} />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 min-w-0">
            <div className="text-sm font-semibold text-gray-800 mb-3">
              Detalhes técnicos
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-start gap-3">
                <FaIdBadge className="text-gray-400 mt-0.5" />
                <Field label="UUID" value={me.uuid} />
              </div>
              <div className="flex items-start gap-3">
                <FaBuilding className="text-gray-400 mt-0.5" />
                <Field label="Username" value={me.username} />
              </div>
              <div className="flex items-start gap-3">
                <FaEnvelope className="text-gray-400 mt-0.5" />
                <Field label="Email pessoal" value={me.personal_email} />
              </div>
              <div className="flex items-start gap-3">
                <FaPhoneAlt className="text-gray-400 mt-0.5" />
                <Field label="Contacto" value={me.contact_number} />
              </div>
              <div className="flex items-start gap-3">
                <FaClock className="text-gray-400 mt-0.5" />
                <Field label="Criado em" value={me.created_at} />
              </div>
              <div className="flex items-start gap-3">
                <FaClock className="text-gray-400 mt-0.5" />
                <Field label="Actualizado em" value={me.updated_at} />
              </div>
            </div>
            <div className="mt-5 text-xs text-gray-500">
              Nota: ainda não temos endpoint de actualização de perfil; esta página é apenas leitura.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

